import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Options } from '@angular-slider/ngx-slider';
import axiosJC261 from 'src/app/Service/axiosJC261';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { OtherService } from 'src/app/Service/other.service';

interface HistoryImage {
  strUnitID: string;
  strCategory: 'outside' | 'cabin';
  strImageUrl: string;
  strThumbnailUrl: string;
  dtImageDateTime: string;
  time: number;
}

@Component({
  selector: 'app-cctv-image-player',
  templateUrl: './cctv-image-player.component.html',
  styleUrls: ['./cctv-image-player.component.css'],
})
export class CctvImagePlayerComponent implements OnInit, OnDestroy {
  readonly unitId = this.route.snapshot.paramMap.get('strUnitID') || '';
  readonly intervals = [1, 3, 6, 12];
  readonly ticks = [0, 1, 2, 3, 4, 5, 6];
  readonly startSliderOptions: Options = {
    floor: 0,
    ceil: 1000,
    hideLimitLabels: true,
    hidePointerLabels: true,
    ariaLabel: 'شروع بازه گالری',
  };
  readonly endSliderOptions: Options = {
    floor: 0,
    ceil: 1000,
    hideLimitLabels: true,
    hidePointerLabels: true,
    ariaLabel: 'پایان بازه گالری',
  };
  images: HistoryImage[] = [];
  timelineImages: HistoryImage[] = [];
  visible: HistoryImage[] = [];
  galleryImages: HistoryImage[] = [];
  galleryPage = 1;
  galleryPageSize = 24;
  readonly galleryPageSizes = [12, 24, 48, 96];
  frames: number[] = [];
  windowStart = 0;
  windowEnd = 0;
  currentTime: number | null = null;
  outside?: HistoryImage;
  cabin?: HistoryImage;
  hours = 0;
  camera: 'both' | 'outside' | 'cabin' = 'both';
  start = 0;
  end = 1000;
  index = 0;
  speed = 1000;
  playing = false;
  loading = false;
  error = '';
  selected: HistoryImage | null = null;
  private timer?: ReturnType<typeof setInterval>;
  private destroyed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guard: GurdServiceService,
    private otherService: OtherService,
  ) {}

  @HostListener('document:keydown.escape')
  closeImage(): void {
    this.selected = null;
  }

  ngOnInit(): void {
    this.loadImages();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.pause();
  }

  async loadImages(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await axiosJC261.post('GetVehicleImageHistory', {
        iSystemUser: Number(this.guard.decrypt(localStorage.getItem('ID'))),
        strSession: this.guard.decrypt(localStorage.getItem('Session')),
        strUnitID: this.unitId,
      });
      if (this.destroyed) return;
      const result =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (result?.ISSessionExpired === true) {
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '1xzOPwsZ' } });
        return;
      }
      if (result?.iResult === 0)
        throw new Error(result.strError || 'دریافت تصاویر ناموفق بود');
      if (!Array.isArray(result))
        throw new Error('پاسخ دریافت تصاویر معتبر نیست');
      this.images = result
        .filter(
          (item: HistoryImage) =>
            item &&
            item.strUnitID === this.unitId &&
            (item.strCategory === 'outside' || item.strCategory === 'cabin') &&
            item.strImageUrl,
        )
        .map((item: HistoryImage) => ({
          ...item,
          time: Date.parse(item.dtImageDateTime),
        }))
        .filter((item: HistoryImage) => Number.isFinite(item.time))
        .sort((a: HistoryImage, b: HistoryImage) => a.time - b.time);
      this.updateTimelineImages();
      this.applyFilter();
    } catch (error) {
      if (!this.destroyed)
        this.error =
          error instanceof Error && error.message !== 'Network Error'
            ? error.message
            : 'اتصال به سرور تصاویر برقرار نشد';
    } finally {
      if (!this.destroyed) this.loading = false;
    }
  }

  position(time: number): number {
    return this.windowEnd === this.windowStart
      ? 0
      : ((time - this.windowStart) / (this.windowEnd - this.windowStart)) * 100;
  }
  seekTime(value: number): void {
    const time = this.rangeTime(value);
    let closest = 0;
    for (let i = 1; i < this.frames.length; i++) {
      if (
        Math.abs(this.frames[i] - time) < Math.abs(this.frames[closest] - time)
      )
        closest = i;
    }
    this.seek(closest);
  }
  imageAge(image: HistoryImage): number {
    return Math.max(
      0,
      Math.round(((this.currentTime ?? image.time) - image.time) / 1000),
    );
  }

  private updateFrameImages(): void {
    this.currentTime = this.frames[this.index] ?? null;
    this.outside = undefined;
    this.cabin = undefined;
    if (this.currentTime === null) return;
    for (let i = this.images.length - 1; i >= 0; i--) {
      const image = this.images[i];
      if (image.time > this.currentTime) continue;
      if (image.strCategory === 'outside' && !this.outside) this.outside = image;
      if (image.strCategory === 'cabin' && !this.cabin) this.cabin = image;
      if (this.outside && this.cabin) break;
    }
  }

  private updateTimelineImages(): void {
    this.windowEnd = this.images.length ? this.images[this.images.length - 1].time : 0;
    this.windowStart = this.images.length
      ? (this.hours ? Math.max(this.images[0].time, this.windowEnd - this.hours * 3600000) : this.images[0].time)
      : 0;
    this.timelineImages = this.images.filter((image) => image.time >= this.windowStart);
  }

  setHours(hours: number): void {
    this.hours = hours;
    this.start = 0;
    this.end = 1000;
    this.updateTimelineImages();
    this.applyFilter();
  }
  setCamera(camera: 'both' | 'outside' | 'cabin'): void {
    this.camera = camera;
    this.applyFilter();
  }
  changeRange(changed: 'start' | 'end'): void {
    if (this.start > this.end) {
      if (changed === 'start') this.end = this.start;
      else this.start = this.end;
    }
    this.applyFilter();
  }
  rangeTime(value: number): number {
    return (
      this.windowStart + ((this.windowEnd - this.windowStart) * value) / 1000
    );
  }
  applyFilter(): void {
    this.pause();
    const start = this.rangeTime(this.start),
      end = this.rangeTime(this.end);
    this.visible = this.images.filter(
      (image) =>
        image.time >= start &&
        image.time <= end &&
        (this.camera === 'both' || image.strCategory === this.camera),
    );
    this.galleryPage = 1;
    this.updateGalleryPage();
    this.frames = Array.from(new Set(this.visible.map((image) => image.time)));
    this.index = Math.max(0, this.frames.length - 1);
    this.updateFrameImages();
  }
  setGalleryPage(page: number): void {
    this.galleryPage = page;
    this.updateGalleryPage();
  }
  setGalleryPageSize(size: number): void {
    this.galleryPageSize = size;
    this.galleryPage = 1;
    this.updateGalleryPage();
  }
  private updateGalleryPage(): void {
    const start = (this.galleryPage - 1) * this.galleryPageSize;
    this.galleryImages = this.visible.slice(start, start + this.galleryPageSize);
  }
  private followCurrentFrame(): void {
    if (this.currentTime === null || this.galleryImages.some(image => image.time === this.currentTime)) return;
    const imageIndex = this.visible.findIndex(image => image.time === this.currentTime);
    if (imageIndex !== -1) this.setGalleryPage(Math.floor(imageIndex / this.galleryPageSize) + 1);
  }
  seek(index: number): void {
    this.pause();
    this.index = Math.max(0, Math.min(index, this.frames.length - 1));
    this.updateFrameImages();
    this.followCurrentFrame();
  }
  selectImage(image: HistoryImage): void {
    this.seek(this.frames.indexOf(image.time));
  }
  togglePlay(): void {
    if (this.playing) {
      this.pause();
      return;
    }
    if (!this.frames.length) return;
    if (this.index === this.frames.length - 1) {
      this.index = 0;
      this.updateFrameImages();
    }
    this.followCurrentFrame();
    this.playing = true;
    this.timer = setInterval(() => {
      if (this.index < this.frames.length - 1) {
        this.index++;
        this.updateFrameImages();
        this.followCurrentFrame();
      } else this.pause();
    }, this.speed);
  }
  pause(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.playing = false;
  }
  changeSpeed(): void {
    if (this.playing) {
      this.pause();
      this.togglePlay();
    }
  }
}
